import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

export type SlidingWindowTone = 'red' | 'purple' | 'blue' | 'amber' | 'neutral';

interface SlidingWindowNavProps {
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Tint chevrons to match the dashboard section accent. */
  tone?: SlidingWindowTone;
  className?: string;
}

const TONE_ENABLED: Record<SlidingWindowTone, string> = {
  red: 'text-red-500 hover:text-red-700 hover:bg-red-50 active:bg-red-100/80 focus-visible:ring-red-500/40',
  purple:
    'text-purple-500 hover:text-purple-700 hover:bg-purple-50 active:bg-purple-100/80 focus-visible:ring-purple-500/40',
  blue: 'text-blue-500 hover:text-blue-700 hover:bg-blue-50 active:bg-blue-100/80 focus-visible:ring-blue-500/40',
  amber:
    'text-amber-500 hover:text-amber-700 hover:bg-amber-50 active:bg-amber-100/80 focus-visible:ring-amber-500/40',
  neutral:
    'text-gray-500 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200/80 focus-visible:ring-gray-400/40',
};

const TONE_DISABLED: Record<SlidingWindowTone, string> = {
  red: 'text-red-200 cursor-not-allowed',
  purple: 'text-purple-200 cursor-not-allowed',
  blue: 'text-blue-200 cursor-not-allowed',
  amber: 'text-amber-200 cursor-not-allowed',
  neutral: 'text-gray-300 cursor-not-allowed',
};

/** Compact up/down chevrons for dashboard card sliding windows. */
const SlidingWindowNav: React.FC<SlidingWindowNavProps> = ({
  canPrev,
  canNext,
  onPrev,
  onNext,
  tone = 'blue',
  className = '',
}) => {
  const { t } = useI18n();

  const btnClass = (enabled: boolean) =>
    `inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors
     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset
     ${enabled ? TONE_ENABLED[tone] : TONE_DISABLED[tone]}`;

  return (
    <div className={`flex items-center gap-0.5 shrink-0 w-[3.75rem] justify-end ${className}`}>
      <button
        type="button"
        disabled={!canPrev}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        className={btnClass(canPrev)}
        aria-label={t('common.previous')}
      >
        <ChevronUp className="w-4 h-4" strokeWidth={2.25} />
      </button>
      <button
        type="button"
        disabled={!canNext}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className={btnClass(canNext)}
        aria-label={t('common.next')}
      >
        <ChevronDown className="w-4 h-4" strokeWidth={2.25} />
      </button>
    </div>
  );
};

export default SlidingWindowNav;
